export function generateLocationKeywords(location: { city?: string; state?: string; area?: string }) {
  const keywords = new Set<string>();
  const { city, state, area } = location;
  const locationString = [area, city, state].filter(Boolean).join(', ');

  // Base variations
  const services = [
    'appliance repair',
    'AC repair',
    'TV repair',
    'washing machine repair',
    'refrigerator repair',
    'RO repair'
  ];

  // Location-based keywords
  if (locationString) {
    const nearMeVariations = [
      'near me',
      'nearby',
      'in my area',
      'around me',
      'close to me',
      'in my locality',
      'nearest to me'
    ];

    const qualityVariations = [
      'best',
      'top',
      'trusted',
      'reliable',
      'professional',
      'expert'
    ];

    const priceVariations = [
      'affordable',
      'cheap',
      'low cost',
      'best price',
      'reasonable price',
      'cost-effective'
    ];

    const timeVariations = [
      '24x7',
      '24/7',
      'same day',
      'emergency',
      'instant',
      'quick',
      'fast'
    ];

    // Generate combinations
    services.forEach(service => {
      // Basic location keywords
      keywords.add(`${service} in ${locationString}`);
      keywords.add(`${locationString} ${service}`);

      // Near me variations
      nearMeVariations.forEach(nearMe => {
        keywords.add(`${service} ${nearMe} ${locationString}`);
      });

      // Quality variations
      qualityVariations.forEach(quality => {
        keywords.add(`${quality} ${service} in ${locationString}`);
      });

      // Price variations
      priceVariations.forEach(price => {
        keywords.add(`${price} ${service} in ${locationString}`);
      });

      // Time variations
      timeVariations.forEach(time => {
        keywords.add(`${time} ${service} in ${locationString}`);
      });
    });
  }

  return Array.from(keywords);
}

export function generateBrandKeywords(brand: string) {
  const keywords = new Set<string>();
  const services = [
    'repair',
    'service',
    'repair service',
    'service center',
    'authorized service',
    'repair center'
  ];

  const qualifiers = [
    'best',
    'affordable',
    'cheap',
    'reliable',
    'trusted',
    'professional',
    'expert',
    '24x7',
    'same day',
    'emergency',
    'near me',
    'in my area'
  ];

  // Basic brand keywords
  services.forEach(service => {
    keywords.add(`${brand} ${service}`);
    qualifiers.forEach(qualifier => {
      keywords.add(`${qualifier} ${brand} ${service}`);
    });
  });

  // Additional brand-specific keywords
  keywords.add(`${brand} customer care`);
  keywords.add(`${brand} support`);
  keywords.add(`${brand} technician`);
  keywords.add(`${brand} spare parts`);
  keywords.add(`${brand} installation`);
  keywords.add(`${brand} maintenance`);
  keywords.add(`${brand} warranty service`);
  keywords.add(`${brand} out of warranty service`);

  return Array.from(keywords);
}

export function generateServiceKeywords(service: string) {
  const keywords = new Set<string>();
  const qualifiers = [
    'best',
    'affordable',
    'cheap',
    'reliable',
    'trusted',
    'professional',
    'expert',
    '24x7',
    'same day',
    'emergency',
    'near me',
    'in my area',
    'at home',
    'doorstep'
  ];

  // Basic service keywords
  qualifiers.forEach(qualifier => {
    keywords.add(`${qualifier} ${service}`);
    keywords.add(`${service} ${qualifier}`);
  });

  // Service-specific keywords
  keywords.add(`${service} cost`);
  keywords.add(`${service} charges`);
  keywords.add(`${service} price`);
  keywords.add(`${service} warranty`);
  keywords.add(`${service} technician`);
  keywords.add(`${service} expert`);
  keywords.add(`${service} company`);
  keywords.add(`home ${service}`);
  keywords.add(`local ${service}`);
  keywords.add(`experienced ${service}`);
  keywords.add(`certified ${service}`);
  keywords.add(`quality ${service}`);

  return Array.from(keywords);
}